import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { API_BASE_URL } from "../lib/api";
import { participantStatusLabel } from "../lib/playerLabels";

// Privacidad (revisión del aviso, 2026-09-29): esta página la ve cualquiera
// que escanee el QR. Solo trae lo necesario para verificar al jugador en la
// cancha: nombre, foto, torneo, categoría, estatus y AÑO de nacimiento (sin
// fecha completa, edad, club ni enlace al archivo de la credencial).
// Multi-categoría (revisión 2026-10-03): un inscrito en varias categorías trae
// UN solo QR; `categories` las lista todas (las dadas de baja no), también las
// de dobles donde juega en pareja (entonces `status` es el de la pareja).
interface CredentialPayload {
  valid: boolean;
  message: string;
  tournament: { id: number; name: string; slug?: string | null };
  participant: {
    id: number;
    status: string;
    photo_url?: string | null;
    category?: string | null;
    categories?: string[] | null;
  };
  player: {
    display_name?: string | null;
    first_name?: string | null;
    last_name?: string | null;
    maternal_last_name?: string | null;
    birth_year?: number | null;
  };
}

function credentialFileUrl(path?: string | null) {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  const appBase = API_BASE_URL.replace(/\/api\/v2$/, "").replace(/\/$/, "");
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return `${appBase}${normalized}`;
}

export default function PublicCredentialProfilePage() {
  const { slug, participantId } = useParams<{ slug: string; participantId: string }>();
  const [data, setData] = useState<CredentialPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!slug || !participantId) return;
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`${API_BASE_URL}/public/tournaments/${slug}/credentials/${participantId}/validate`, { cache: "no-store" });
        const payload = await response.json();
        if (!response.ok || !payload.ok) throw new Error(payload?.detail?.message ?? payload?.message ?? "Credencial no encontrada");
        if (!cancelled) setData(payload.data);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : "No pudimos verificar la credencial.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => { cancelled = true; };
  }, [slug, participantId]);

  if (loading) return <section className="card"><p className="muted">Verificando credencial…</p></section>;
  if (error) return <section className="card"><p className="form-error">{error}</p></section>;
  if (!data) return null;

  const photoUrl = credentialFileUrl(data.participant.photo_url);
  const categories = data.participant.categories ?? (data.participant.category ? [data.participant.category] : []);
  const fullName = data.player.display_name || [data.player.first_name, data.player.last_name, data.player.maternal_last_name].filter(Boolean).join(" ") || "Jugador";

  return (
    <section className="public-credential-profile">
      <div className="public-credential-profile__hero">
        <div className="public-credential-profile__hero-copy">
          <p className="eyebrow">Credencial verificable Torneos123</p>
          <h1>{fullName}</h1>
          <p>{data.message}</p>
          <span className="status-chip status-chip--success">Válida</span>
        </div>
        {photoUrl ? (
          <img
            className="public-credential-profile__photo"
            src={photoUrl}
            alt={`Foto de ${fullName}`}
            loading="eager"
          />
        ) : null}
      </div>
      <div className="public-credential-profile__grid">
        <article className="card">
          <h2 className="card__title">Perfil del jugador</h2>
          <dl className="public-credential-profile__facts">
            <div><dt>Torneo</dt><dd>{data.tournament.name}</dd></div>
            <div><dt>{categories.length > 1 ? "Categorías" : "Categoría"}</dt><dd>{categories.join(", ") || "—"}</dd></div>
            <div><dt>Año de nacimiento</dt><dd>{data.player.birth_year ?? "—"}</dd></div>
            <div><dt>Estatus inscripción</dt><dd>{participantStatusLabel(data.participant.status)}</dd></div>
          </dl>
        </article>
        <article className="card">
          <h2 className="card__title">Documento oficial</h2>
          {/* Revisión 2026-10-03: la credencial impresa NO trae categoría (trae
              torneo, foto, nombre, fecha de nacimiento, edad y QR). Se compara
              solo lo que sí trae; la categoría se revisa contra esta página. */}
          <p className="muted">Compara la foto y el nombre con la credencial impresa que trae el jugador; el año de nacimiento debe coincidir con la fecha impresa. La categoría del partido debe aparecer en esta página.</p>
        </article>
      </div>
      <Link className="button button--ghost" to="/">Ir a Torneos123</Link>
    </section>
  );
}
